import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Zap, ArrowLeft, Loader2, Phone, Mail, Calendar, Shield, Search, User, IndianRupee
} from "lucide-react";
import { authGet, isAdvisor } from "../utils/auth";
import MobileBottomNav from "../components/MobileBottomNav";

export default function XRayLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isAdvisor()) { navigate("/advisor/dashboard"); return; }
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await authGet("/api/advisor/policy-xray/leads");
      if (res.ok) setLeads(res.data?.leads || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filtered = leads.filter(l =>
    (l.policyholder_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.company_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.contact_phone || "").includes(search)
  );

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-pink-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] pb-24">
      <header className="bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[#3A4557] hover:text-[#1B2A4A]" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#1B2A4A]">Policy X-Ray Leads</h1>
              <p className="text-[#3A4557] text-xs">{leads.length} {leads.length === 1 ? "person" : "people"} scanned their policy</p>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-3xl mx-auto">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
          <input
            type="text" placeholder="Search by name, company, phone..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6]/50 rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#3A4557] focus:outline-none focus:ring-2 focus:ring-pink-500/30"
            data-testid="search-leads"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Zap className="h-12 w-12 text-[#3A4557] mx-auto mb-3" />
            <p className="text-[#3A4557] font-medium">{search ? "No matches" : "No X-Ray leads yet"}</p>
            <p className="text-[#3A4557] text-sm mt-1">When someone scans their policy via Policy X-Ray, they'll appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((lead, idx) => (
              <motion.div
                key={lead.id || idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="bg-[#EDF1F8]/40 border border-[#C9D4E6]/50 rounded-2xl p-4"
                data-testid={`xray-lead-${lead.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pink-500/15 rounded-xl flex items-center justify-center shrink-0">
                      <User className="h-5 w-5 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="text-[#1B2A4A] font-semibold text-sm">{lead.policyholder_name || "Unknown"}</h3>
                      <p className="text-[#3A4557] text-xs">{lead.company_name} — {lead.product_name}</p>
                    </div>
                  </div>
                  {lead.overall_score && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      lead.overall_score >= 8 ? "bg-emerald-500/15 text-emerald-600" :
                      lead.overall_score >= 5 ? "bg-[#D4A017]/15 text-[#A87608]" :
                      "bg-red-500/15 text-red-600"
                    }`}>
                      Score: {lead.overall_score}/10
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  {lead.contact_phone && (
                    <a href={`tel:${lead.contact_phone}`} className="flex items-center gap-1.5 text-xs text-[#3A4557] hover:text-emerald-600 transition-colors">
                      <Phone className="h-3 w-3" /> {lead.contact_phone}
                    </a>
                  )}
                  {lead.contact_email && (
                    <a href={`mailto:${lead.contact_email}`} className="flex items-center gap-1.5 text-xs text-[#3A4557] hover:text-[#2E3B57] transition-colors truncate">
                      <Mail className="h-3 w-3" /> {lead.contact_email}
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-white/50 rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[#3A4557] text-[10px] uppercase">Sum Insured</p>
                    <p className="text-[#1B2A4A] text-xs font-medium mt-0.5">{lead.sum_insured || "—"}</p>
                  </div>
                  <div className="bg-white/50 rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[#3A4557] text-[10px] uppercase">Premium</p>
                    <p className="text-[#1B2A4A] text-xs font-medium mt-0.5">{lead.premium || "—"}</p>
                  </div>
                  <div className="bg-white/50 rounded-lg px-2 py-1.5 text-center">
                    <p className="text-[#3A4557] text-[10px] uppercase">Expiry</p>
                    <p className="text-[#A87608] text-xs font-medium mt-0.5">{formatDate(lead.policy_expiry)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#C9D4E6]/30">
                  <span className="text-[#3A4557] text-xs">{formatDate(lead.created_at)}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#E2E9F4]/50 text-[#3A4557]">
                    {lead.insured_members_count || 0} member{(lead.insured_members_count || 0) !== 1 ? 's' : ''}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
}
